import { createContext, useContext, useEffect, useState } from "react";
import { createPart, getParts, removePart } from "../services/PartsConfigurationService";
import { useSnackBar } from "../../../hooks/useSnackBar";
import { localStorageRemove, localStorageSave } from "../../../services/localStorageService";

const PartConfigurationContext = createContext();

const localStoragePrefix = 'configuration/parts/draft';

const dropdownColumnMap = {
  1: "id",
  2: "categoryId",
  3: "subCategoryId",
  11: "ATAChapterNumber",
  12: "ATASectionNumber",
  13: "ATAParagraphNumber"
}

const pageSize = 10;

const rowUpdater = {'general':{
  categoryId: (value) => ({ categoryName: value.name, subCategoryName: null }),
  subCategoryId: (value) => ({ subCategoryName: value.name }),
  atachapterId: (value) => ({ ATAChapterNumber: value.chapter_number, ATASectionNumber: null, ATAParagraphNumber: null }),
  atasectionId: (value) => ({ ATASectionNumber: value.section_number,ATAParagraphNumber: null }),
  ataparagraphId: (value) => ({ ATAParagraphNumber: value.paragraph_number }),

}}

const PartConfigurationProvider = ({ children }) => {
  const [partList, setPartList] = useState([]);
  const [filters, setFilters] = useState({ dropdowns: {}, txt: {} });
  const [idxList, setIdxList] = useState({});
  const [idxListBackup, setIdxListBackup] = useState({});
  const [drafts, setDrafts] = useState(getSectionsByPrefix(localStoragePrefix));
  const [page, setPage] = useState(0);
  const [maxPages, setMaxPages] = useState(null);
  const [newDrafts, setNewDrafts] = useState({});
  const [loadingParts, setLoadingParts] = useState(false);
  const { successMsg, warningMsg, infoMsg } = useSnackBar();


  const onFiltersChange = (dropdownFilters, txtFilters) => {
    setFilters({
      dropdowns: dropdownFilters,
      txt: txtFilters
    });
  }

  const onSearch = (value) => {
    setFilters(prev => ({ ...prev, txt: { ...prev.txt, search: value } }));
  }

  const updateValue = (partId, name, value )=> {
   setIdxList(prev => ({...prev, [partId]: {...prev[partId], [name]:value}}));
  }
  useEffect(()=>{
  },[
    drafts
  ])

  // Draft methods
  const saveDraft = (partId, section, value) => {
    // localStorage.setItem(`${localStoragePrefix}/${section}?id=${partId}`, JSON.stringify(value));
    // localStorageSave(`${localStoragePrefix}/${section}`, { partId: partId }, value);
    setDrafts(prev => ({ ...prev, [partId]: (prev[partId] ?? new Set()).add(section) }));
  }
  const isPartDraft = (id) => id in drafts;
  // const isPartSectionDraft = (id, section) => drafts[id]?.has(se;
  const isPartSectionDraft = (id, section) => [...(drafts[id] ?? [])].some(str => str.includes(section));
  const removeDraft = (partId, section) => {
    // localStorage.removeItem(`${localStoragePrefix}/${section}?id=${partId}`);
    localStorageRemove(`${localStoragePrefix}/${section}`, { partId: partId.toString() });
    setDrafts(prev => {
      let prevSet = prev[partId] ?? new Set();
      prevSet.delete(section);
      if (prevSet.size > 0) {
        return { ...prev, [partId]: prevSet }
      } else {
        let prevDrafts = { ...prev };
        delete prevDrafts[partId];
        return prevDrafts
      }
    });
  }

  const resetPart = (partId) => {
    if (partId in newDrafts) {
      // infoMsg("Draft discarded");
      setPartList(prev => prev.filter(p => p.id !== partId));
      setIdxList({ ...idxListBackup });
      removeDraft(partId, 'row');
    } else {
      setIdxList(prev => ({ ...prev, [partId]: idxListBackup[partId] }));
      removeDraft(partId, 'row');
    }
  }

  const confirmPart = (partId) => {
    setIdxListBackup(prev => ({ ...prev, [partId]: idxList[partId] }));
    removeDraft(partId, 'row');
  }

  useEffect(() => {
    setLoadingParts(true);
    getParts(filters, dropdownColumnMap, page, pageSize).then(response => {
      if (response.data.responseCode === 200) {
        setMaxPages(response.data.totalPages);
        let partArray = response.data.load;
        let rowDrafts = getObjectsBySection(localStoragePrefix, 'row');
        let newItems = Object.fromEntries(Object.entries(rowDrafts).filter(([draftId, draft]) => !partArray.some(item => item.id === draftId)));
        setNewDrafts(newItems);
        setIdxList(prev => {
          let obj = partArray.reduce((acc, current) => ({ ...acc, [current.id]: { ...current, ...(rowDrafts[current.id] ?? {}) } }), {});
          return page > 0 ? { ...prev, ...obj } : {...newItems, ...obj};
        });
        setIdxListBackup(prev => {
          let obj = partArray.reduce((prev, current) => ({ ...prev, [current.id]: current }), {});
          return page > 0 ? { ...prev, ...obj } : {...newItems, ...obj};
        });
        setPartList(prev => page > 0 ? [...prev, ...partArray] :[...Object.values(newItems) ,...partArray]);
      }
      setLoadingParts(false);
    }).catch(error => setLoadingParts(false));
  }, [filters, page]);

  const updateRow = (id, partialValue) => {
    setIdxList(prev => {
      if (isPartDraft(id)) {
        saveDraft(id, 'row', { ...prev[id], ...partialValue });
      }
      return ({ ...prev, [id]: { ...prev[id], ...partialValue } })
    });
    setNewDrafts((prev) => {
      return { ...prev, [id]: { ...prev[id], ...partialValue } };
    });
  }
  
  const updateSection = (formValue, partId, section) => {
    let { name, value } = formValue;
    updateRow(partId, rowUpdater[section]?.[name]?.(value) ?? { [name]: value });
  }

  const requestNextPage = () => {
    setPage(prev => Math.min(prev + 1, maxPages - 1));
  }

  const addPart = () => {
    infoMsg("Creating draft", 6000);
    createPart({ id: null }).then(response => {
      if (response.data.responseCode === 200) {
        localStorage.setItem(localStoragePrefix + '/new-auto-open', 'true');
        let createdPart = response.data.load;
        setIdxList(prev => ({ ...prev, [createdPart.id]: createdPart }));
        saveDraft(createdPart.id, 'general', createdPart);
        saveDraft(createdPart.id, 'row', {id: createdPart.id});
        setNewDrafts(prev => ({...prev, [createdPart.id]:{id: createdPart.id}}))
        // setIdxListBackup(response.data.load.reduce((prev, current) => ({ ...prev, [current.id]: current }), {}));
        setPartList(prev => [createdPart, ...prev]);
      }
    }).catch(error => { })
  }
  const isNewDraft = (partId) => partId in newDrafts; 

  const deletePart = (partId) => {
    isNewDraft(partId) ? resetPart(partId):
    removePart(partId).then(response => {
      if (response.data.responseCode === 200) {
        successMsg('Part deleted successfully');
        setIdxList(prev => {
          let copy = { ...prev };
          delete copy[partId];
          return copy;
        });
        setIdxListBackup(prev => {
          let copy = { ...prev };
          delete copy[partId];
          return copy;
        })
        setPartList(prev => prev.filter(part => part.id !== partId));
      } else {
        warningMsg(response.data.message || `Something went wrong: ${response.data.responseCode}`);
      }
    }).catch(error => warningMsg(`Something went wrong: ${error}`));

  }


  return (
    <PartConfigurationContext.Provider value={{ partList, idxList, updateSection, isPartDraft, isPartSectionDraft, saveDraft, removeDraft, resetPart, confirmPart, onFiltersChange, onSearch, addPart, requestNextPage, setLoadingParts, deletePart, isNewDraft, updateValue }}>
      {children}
    </PartConfigurationContext.Provider>
  )
}


function getSectionsById(id, prefix) {
  return [...Array(localStorage.length).keys()]
    .map(i => localStorage.key(i))
    .filter(key => key.startsWith(prefix) && key.includes(`?id=${id}`))
    .map(key => key.split('/')[3].split('?')[0]);
}

function getSectionsByPrefix(prefix) {
  return [...Array(localStorage.length).keys()].map(i => localStorage.key(i))
    .filter(key => key.startsWith(prefix)).reduce((acc, key) => {
      const queryParams = new URLSearchParams(key.split('?')[1]);
      if (localStorage.getItem('u_id') === queryParams.get('userId')) {
        let id = queryParams.get("partId");
        if (!acc[id]) {
          acc[id] = new Set();
        }
        acc[id].add(key.split("?")[0].replace(prefix + '/', ""));
      }
      return acc;
    }, {});

}

function getObjectsBySection(prefix, section) {
  return [...Array(localStorage.length).keys()].map(i => localStorage.key(i)).filter(key => key.startsWith(prefix + '/' + section))
    .reduce((acc, key) => {
      const queryParams = new URLSearchParams(key.split('?')[1]);
      if (localStorage.getItem('u_id') === queryParams.get('userId')) {
        const id = queryParams.get('partId');
        acc[id] = JSON.parse(localStorage.getItem(key));
      }
      return acc;
    }, {})
}

export { PartConfigurationContext, PartConfigurationProvider }

export const usePartValue = (part) => {
  const {idxList} = useContext(PartConfigurationContext);
  return idxList[part.id];
}